import { useMutation } from '@apollo/client';
import ConfirmationModal from 'components/ConfirmationModal';
import { EmptyStateWithText } from 'components/EmptyState';
import AsideListItem from 'components/Layout/components/BaseDataAside/AsideListItem';
import { useContextBaseDataAside } from 'components/Layout/components/BaseDataAside/context';
import { BaseDataForm } from 'components/Layout/components/BaseDataAside/submenu';
import { TabPane } from 'components/Layout/components/common/styled-components';
import { useContextLayout } from 'components/Layout/context';
import { useContextSnackbar } from 'components/Snackbar/context';
import routePaths from 'constants/route-paths';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { matchPath, useLocation } from 'react-router-dom';
import { DELETE_WELFARE_OFFICE } from 'services/mutation/welfareOffices';
import { WelfareOfficeListData } from 'services/welfareOffices';

const BaseDataWelfareOfficeAsideMenu = () => {
  const { t } = useTranslation();
  const tabPaneRef = useRef(null);
  const { setActiveUser, setActiveMenu } = useContextBaseDataAside();
  const location = useLocation();
  const history = useHistory();
  const { filteredWelfareOffices, setShouldRefetch } = useContextLayout();

  const { showApiError } = useContextSnackbar();

  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [selectedWelfareOffice, setSelectedWelfareOffice] = useState<
    WelfareOfficeListData | undefined
  >();

  const [deleteWelfareOffice] = useMutation(DELETE_WELFARE_OFFICE);

  const userPaths = matchPath(location.pathname, {
    path: routePaths.DATA_WELFARE_OFFICE,
  }) as { params: { entityId: string } };

  const pathName = location.pathname;

  useEffect(() => {
    const params = {
      entityId: userPaths?.params?.entityId,
    } as { entityId: string };

    if (params.entityId) {
      setActiveUser(params.entityId);
      if (pathName.includes(routePaths.DATA_ACCESS)) {
        setActiveMenu(BaseDataForm.SYSTEM_ACCESS);
      } else if (pathName.includes(routePaths.DATA_CONTRACT)) {
        setActiveMenu(BaseDataForm.CONTRACT);
      } else {
        setActiveMenu(BaseDataForm.GENERAL);
      }
    }
  }, [setActiveMenu, setActiveUser, pathName, userPaths]);

  const onDelete = (welfareOffice: WelfareOfficeListData) => {
    const name = `${welfareOffice.name}`;
    setConfirmationText(t('welfare_office_deleteConfirmation', { name }));
    setShowConfirmation(true);
    setSelectedWelfareOffice(welfareOffice);
  };

  const handleClose = (response: boolean) => {
    setShowConfirmation(false);
    if (!response || !selectedWelfareOffice) {
      setSelectedWelfareOffice(undefined);
      return;
    }
    deleteWelfareOffice({ variables: { id: selectedWelfareOffice.id } })
      .then(() => {
        setShouldRefetch('deleted');
        history.push(routePaths.BASE_DATA);
      })
      .catch((error) => {
        showApiError(error);
      });
  };

  return (
    <>
      <TabPane ref={tabPaneRef}>
        <div className="aside-menu">
          <ul className="menu-nav py-0">
            {filteredWelfareOffices.length ? (
              filteredWelfareOffices.map((user: WelfareOfficeListData) => (
                <AsideListItem
                  key={user.id}
                  id={user.id}
                  label={user.name}
                  description=""
                  warning={1}
                  tabPaneRef={tabPaneRef}
                  routePath={routePaths.DATA_WELFARE_OFFICE}
                  showDelete={true}
                  isDeleted={!!user.deletedAt}
                  onDelete={() => onDelete(user)}
                />
              ))
            ) : (
              <EmptyStateWithText text={t('no_results_for_current_settings')} verticalPositionTop />
            )}
          </ul>
        </div>
      </TabPane>
      <ConfirmationModal show={showConfirmation} text={confirmationText} onResponse={handleClose} />
    </>
  );
};

export default BaseDataWelfareOfficeAsideMenu;
